import React, { useState, useRef, useEffect } from "react";
import "./login_style.scss"; 
import appBgGradient from '../../../assets/app_launchscreen.png';
import appLogo from '../../../assets/anytrakk.png';
import { toast } from 'react-toastify';
import { getOTP, loginUser } from './action.ts';
import 'react-toastify/dist/ReactToastify.css';
import { useSearchParams } from "react-router-dom";
import { useNavigate } from 'react-router-dom'; 

interface LoginScreenProps {
}

const LoginScreen: React.FC<LoginScreenProps> = () => {
  const [searchParams] = useSearchParams();
  const userName = searchParams.get('user_name');
  const userPhone = searchParams.get('user_phone');
  const userId = searchParams.get('user_id');
  const fcmToken: string | undefined = searchParams.get('fcm_token') ?? undefined;
  
  const [countryCode, setCountryCode] = useState('91');
  const [phoneNumber, setPhoneNumber] = useState(userPhone ?? '');
  const [otpCode, setOtpCode] = useState("");
  const [tPin, setTPin] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [loadingOTP, setLoadingOTP] = useState(false);
  
  const otpRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];
  
  const pinRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];
  
  const navigate = useNavigate();

  // Effect to automatically trigger login when 4th digit is entered
  useEffect(() => {
    const autoLogin = async () => {
      if ((otpCode.length === 4 || tPin.length === 4) && !isLoading) {
        // Validate inputs before calling API
        if (!phoneNumber || phoneNumber.length !== 10) {
          toast.error("Please enter a valid 10-digit phone number");
          return;
        }
        
        await handleLogin();
      }
    };
    
    autoLogin();
  }, [otpCode, tPin]); // Run when otpCode or tPin changes

  const tapOnSendOTP = () => {
    if (!phoneNumber) {
      toast.error("Phone number is required");
    } else if (phoneNumber.length < 10) {
      toast.error("Phone number must be 10 digits");
    } else {
      sendOTPAPI();
    }
  };

  const sendOTPAPI = () => {
    getOTP(phoneNumber, '91', '0',
      (response) => {
        if (response.data.status === 200) {
          toast.success(response.data.message);
          // Focus on first OTP input after sending OTP
          if (otpRefs[0].current) {
            otpRefs[0].current.focus();
          }
        } else {
          toast.error(response.data.message);
        }
      },
      (errorMessage) => {
        toast.error(errorMessage.message);
      }
    );
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      await loginUser(phoneNumber, '91', 'EN', 
        (response) => {
          if (response.data.status === 200) {
            toast.success(response.data.message);
            localStorage.setItem("user_phone", phoneNumber);
            localStorage.setItem("token", response.data.token);
            
            if ((window as any).ReactNativeWebView) {
              const message = {
                type: 'LOGIN_SUCCESS',
                data: {
                  token: response.data.token,
                  user: response.data.user,
                  role: response.data.role,
                }
              };
              (window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
            }
            
            const newUrl = `/wv/dashboard?token=${response.data.token}&user_name=${response.data.user.name}&user_role=${response.data.role}&user_id=${response.data.user.id}&user_phone=${phoneNumber}`;
            navigate(newUrl);
          } else {
            toast.error(response.data.message);
            setLoading(false);
          }
        },
        (errorMessage) => {
          toast.error(errorMessage.message);
          setLoading(false);
        },
        otpCode,
        tPin,
        fcmToken
      );
    } catch (error) {
      toast.error("An error occurred during login");
      setLoading(false);
    }
  };

  const loginPressHandler = () => {
    handleLogin();
  };

  const handleOtpChange = (index: number, value: string) => {
    const newOtp = otpCode.split("");
    newOtp[index] = value;
    const joinedOtp = newOtp.join("").slice(0, 4);
    
    // Clear PIN if OTP is being entered
    if (value && tPin.length > 0) {
      setTPin("");
    }
    
    setOtpCode(joinedOtp);
    
    // Auto-focus next input
    if (value && index < 3) {
      setTimeout(() => {
        otpRefs[index + 1].current?.focus();
      }, 10);
    }
  };

  const handlePinChange = (index: number, value: string) => {
    const newPin = tPin.split("");
    newPin[index] = value;
    const joinedPin = newPin.join("").slice(0, 4);
    
    // Clear OTP if PIN is being entered
    if (value && otpCode.length > 0) {
      setOtpCode("");
    }
    
    setTPin(joinedPin);
    
    // Auto-focus next input
    if (value && index < 3) {
      setTimeout(() => {
        pinRefs[index + 1].current?.focus();
      }, 10);
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
    
    // Allow Enter key to trigger login from any OTP input
    if (e.key === 'Enter' && (otpCode.length === 4 || tPin.length === 4)) {
      loginPressHandler();
    }
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !tPin[index] && index > 0) {
      pinRefs[index - 1].current?.focus();
    }
    
    // Allow Enter key to trigger login from any PIN input
    if (e.key === 'Enter' && (otpCode.length === 4 || tPin.length === 4)) {
      loginPressHandler();
    }
  };

  return (
    <div className="base-container" style={{
      background: `url(${appBgGradient}) no-repeat center center fixed`,
      backgroundSize: 'cover'
    }}>
      <div className="inner-container">
        <div className="login-logo-container">
          <img src={appLogo} alt="App Logo" className="logo-image" />
        </div>
        
        <div className="login-white-card">
          <div className="title-content">
            <h2>{userName}</h2>
          </div>
          
          <div className="input-section">
            <div className="phone-input-container">
              <button 
                className="country-code-button"
                onClick={() => console.log("Country code picker clicked")}
              >
                {countryCode}
              </button>
              <input
                type="tel"
                className="phone-input"
                placeholder="Phone Number"
                value={phoneNumber}
                disabled
              />
            </div>
            
            <div className="pin-section">
              <div className="otp-container">
                <label className="pin-label">PIN</label>
                {[0, 1, 2, 3].map((index) => (
                  <input
                    key={index}
                    ref={pinRefs[index]}
                    className="otp-input"
                    type="number"
                    maxLength={1}
                    value={tPin[index] || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      handlePinChange(index, val);
                    }}
                    onKeyDown={(e) => handlePinKeyDown(index, e)}
                  />
                ))}
              </div>
            </div>
            
            <div className="or-container">
              <div className="line" />
              <span className="or-text">OR</span>
              <div className="line" />
            </div>
            
            {/* <div className="otp-section">
              <div className="otp-container">
                <button 
                  className="inline-otp-button" 
                  onClick={tapOnSendOTP} 
                  disabled={loadingOTP}
                >
                  {loadingOTP ? "Sending..." : "Get OTP"}
                </button>
                {[0, 1, 2, 3].map((index) => (
                  <input
                    key={index}
                    ref={otpRefs[index]}
                    className="otp-input"
                    type="number"
                    maxLength={1}
                    value={otpCode[index] || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      handleOtpChange(index, value);
                    }}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  />
                ))}
              </div>
            </div> */}
          </div>
          
          
          {isLoading && (otpCode.length === 4 || tPin.length === 4) && (
            <div className="auto-login-message">
              Logging in 
            </div>
          )}

          <div className="language-dropdown">
            <button className="language-button">
              English
              <span className="chevron-down">▼</span>
            </button>
          </div>
          <div style={{ marginTop: "30px", textAlign: "center" }}>
            <label className="policy-label">
              <a href="/wv/privacy_policy" className="policy-link">
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="/wv/terms_of_service"
                className="policy-link"
              >
                Terms of Service
              </a>{" "}
              apply.
            </label>
          </div>

        </div>
        
        <div className="language-dropdown">
          <button className="language-button">
            English
            <span className="chevron-down">▼</span>
          </button>
        </div>
        
      
      </div>
    </div>
  );
};

export default LoginScreen;